<svelte:options
  customElement={{
    tag: "oc-expander-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      variant: { type: "String" },
      cutoffHeight: { type: "Number", attribute: "cutoff-height" },
      expanded: { type: "Boolean" },
      overflows: { type: "Boolean" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      hideFade: { type: "Boolean", attribute: "hide-fade" },
    },
  }}
/>

<script lang="ts">
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { untrack } from "svelte";
  import type { Events, Props } from "./ExpanderV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    variant = "row",
    cutoffHeight = 150,
    expanded = false,
    /*                                                         */
    overflows,
    hideFade = false,
    isCutOff = false,
    ocAriaLabel,
  }: Props = $props();

  const Host = $host();
  const dispatch = useEventDispatcher<Events>(Host);
  let clientHeight: number = $state(0);

  $effect(() => {
    overflows = isCutOff && !untrack(() => expanded);
    dispatch(
      "oc-expander-content-overflow-change",
      { overflows, cutoffHeight },
      { cancelable: false, alias: ["oc-content-overflow-change"] },
    );
    isCutOff = clientHeight > (cutoffHeight ?? 150);
  });

  /**
 *
 *
 */
  let displayFade = $derived(isCutOff && !expanded);

  function onClick(ev: MouseEvent): void {
    ev.preventDefault();

    const nextExpanded = !expanded;

    /*                                   */
    /*                         */
    if (
      !dispatch(
        nextExpanded ? "oc-expander-open" : "oc-expander-close",
        { hideFade, variant },
        {
          alias: [nextExpanded ? "oc-open" : "oc-close"],
        },
      )
    ) {
      return;
    }

    /*                                                  */
    expanded = nextExpanded;

    if (!expanded) {
      let top = Host.getBoundingClientRect().top;
      if (top < 0) {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          Host.scrollIntoView({ block: "start", behavior: "instant" });
        } else {
          Host.scrollIntoView({ block: "start", behavior: "smooth" });
        }
      }
    }
  }
</script>

<div class={`expander expander--variant-${variant}`}>
  <div class="expander__container">
    <section
      class="expander__content"
      class:expander__content--open={expanded}
      style:--cutoff-height="{cutoffHeight}px"
      id="content"
      aria-label={ocAriaLabel}
    >
      <div bind:clientHeight>
        <slot />
      </div>
    </section>
    {#if isCutOff}
      <div id="hint">
        Für Screenreader wird der gesamte Inhalt bereits angezeigt, für sehende Nutzer:innen wird
        der Inhalt ein- bzw. ausgeklappt.
      </div>
      <!-- Display divider when row and expanded   -->
      {#if variant === "row" && expanded}
        <oc-divider-v1 alignment="bottom"></oc-divider-v1>
        <!-- Display divider when row, collapsed and no visible fade only -->
      {:else if variant === "row" && !expanded && hideFade}
        <oc-divider-v1 alignment="bottom"></oc-divider-v1>
      {/if}

      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <oc-row-v2
        onclick={onClick}
        as-button
        class="trigger"
        class:oc-row-v1--hide-divider={variant === "text"}
        hide-divider={true}
        aria-expanded={expanded}
        aria-describedby="hint"
        aria-controls="content"
      >
        {#if displayFade && !hideFade}
          <div class="fade"></div>
        {/if}

        <div class="trigger__text">
          {#if expanded}
            <slot name="show-less-text">Weniger anzeigen</slot>
          {:else}
            <slot name="show-more-text">Mehr anzeigen</slot>
          {/if}
          <oc-icon-v1 type="arrow-down"></oc-icon-v1>
        </div>
      </oc-row-v2>
    {/if}
  </div>
</div>

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "../../../css-only-components/row/v1/RowV1.global" as row;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  :host {
    --background-color: #{tokens.$oc-component-expander-background-color};
  }

  .expander {
    display: flex; /*                                       */
    background: var(--background-color);

    .trigger {
      font: tokens.$oc-component-expander-font;

      &__text {
        display: flex;
        justify-content: space-between;
        > slot {
          color: tokens.$oc-component-expander-text-color;
          display: block;
          flex: 1;
        }

        & > oc-icon-v1 {
          --color: #{tokens.$oc-component-expander-icon-color};
          flex: 0 0 auto;
          transition: transform tokens.$oc-component-expander-transition-duration
            tokens.$oc-component-expander-transition-easing;
        }
      }

      &[aria-expanded="true"] oc-icon-v1 {
        transform: rotate(-180deg);
      }
    }

    &--variant-text {
      .trigger__text {
        flex-direction: row-reverse;
        gap: tokens.$oc-component-expander-gap-x;
      }
    }
  }

  .expander__container {
    width: 100%;
  }

  .expander__content {
    position: relative;
    transition: max-height tokens.$oc-component-expander-transition-duration
      tokens.$oc-component-expander-transition-easing;
    max-height: var(--cutoff-height);
    overflow: hidden;

    @media (prefers-reduced-motion) {
      transition: none;
    }
  }

  .expander__content--open {
    max-height: 100%;
    overflow: visible;
  }
  #hint {
    @include mixins.visually-hidden();
  }

  .fade {
    background: var(--background-color);
    mask: linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0.75) 50%, #fff0 100%);
    top: -6.25rem;
    cursor: pointer;
    height: tokens.$oc-component-expander-fade-height;
    left: 0;
    position: absolute;
    width: 100%;
  }
</style>
